import React, { JSX, useContext, useEffect, useState } from "react"
import { cloneDeep, isEqual } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { MedicaidFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";
import MedicaidNetworkFiltersTable from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidNetworkFiltersTable";
import { MedicaidParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganization";
import { MedicaidParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganizationSortingTypeEnum";

const MedicaidNetworkFilters = (): JSX.Element => {
  const [sortedParentOrganizations, setSortedParentOrganizations] = useState<MedicaidParentOrganization[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const medicaidFiltersContext = useContext(MedicaidFiltersContext);
  const benchmarkingContext = useContext(BenchmarkingContext);

  useEffect(() => {
    if (!!medicaidFiltersContext.selectedSortType) {
      const parentOrgs = medicaidFiltersContext.sortNetworks(cloneDeep(medicaidFiltersContext.parentOrganizations)) as MedicaidParentOrganization[];
      setSortedParentOrganizations(parentOrgs);
    }
  }, [medicaidFiltersContext.selectedSortType]);

  const clearDisabled = (): boolean => {
    if (!medicaidFiltersContext.networkFiltersLoading && !!benchmarkingFiltersContext.selectedNetworks.length) {
      return false;
    }

    return true;
  }

  const submitDisabled = (): boolean => {
    const networksEqual = isEqual(
      cloneDeep(benchmarkingFiltersContext.displayedNetworkFilters).sort((a, b) => a.contractId.localeCompare(b.contractId)),
      cloneDeep(benchmarkingFiltersContext.selectedNetworks).sort((a, b) => a.contractId.localeCompare(b.contractId))
    );

    return (
      medicaidFiltersContext.networkFiltersLoading ||
      benchmarkingFiltersContext.selectedNetworks.length <= 1 ||
      benchmarkingFiltersContext.selectedNetworks.length >= 6 ||
      networksEqual
    );
  }

  return (
    <React.Fragment>
      {!medicaidFiltersContext.networkFiltersLoading ? (
        <React.Fragment>
          <div id="benchmarking-dashboard__network-filters--container">
            <hr className="w-100" />
            <div className="row w-100">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <label className="label-m mb0 d-block">
                      Comparison Networks
                    </label>

                    <label className={`copy-text-default d-block mb-3 ${!benchmarkingFiltersContext.displayedPrimaryFilters && "hidden-instructions"}`}>
                      Please select at least 2 and up to 5 networks for comparison.
                    </label>
                  </div>

                  <div>
                    <QESSelect
                      containerClassName="mb-3"
                      testId="network-benchmarking-dashboard__network-filters--parent-organization-sort-by"
                      label="Sort By"
                      onChange={(e) => medicaidFiltersContext.setSelectedSortType(e.target.value as MedicaidParentOrganizationSortingTypeEnum)}
                      value={medicaidFiltersContext.selectedSortType || ""}
                    >
                      {Object.values(MedicaidParentOrganizationSortingTypeEnum).map((sortingType, index) => (
                        <option
                          key={index}
                          value={sortingType}
                        >
                          {sortingType}
                        </option>
                      ))}
                    </QESSelect>
                  </div>
                </div>
              </div>
            </div>
            <div className="row w-100 mt-3" id="benchmarking-dashboard__network-filters--networks">
              {!!sortedParentOrganizations.length && (
                sortedParentOrganizations.map((parentOrganization, index) => (
                  <div className="col-md-4 benchmarking-network" key={index}>
                    <label className="label-s mb-0 mt-1">
                      {parentOrganization.parentOrganizationName}
                    </label>

                    <MedicaidNetworkFiltersTable
                      data={[parentOrganization.hContract]}
                      parentOrganizationName={parentOrganization.parentOrganizationName}
                      selectedContractIds={benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-end mt-3">
                <QESButton
                  disabled={clearDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--clear"
                  qesType="secondary-danger"
                  onClick={() => medicaidFiltersContext.onClearNetworkFiltersClick()}
                  className="me-3"
                >
                  Clear
                </QESButton>

                <QESButton
                  disabled={submitDisabled()}
                  qesTestId="network-benchmarking-dashboard__network-filters--submit"
                  qesType="primary"
                  onClick={() => benchmarkingContext.onSubmitNetworkFiltersClick(medicaidFiltersContext.parentOrganizations)}
                >
                  Submit
                </QESButton>
              </div>
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center mt-3">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default MedicaidNetworkFilters;
